import React, { FC } from 'react';
import { Button } from 'antd';
import { CheckIcon } from 'assets/svg/CheckIcon';
import ArrowRight from 'assets/svg/ArrowRight';

export type WorkflowCardVariant = 'warning' | 'success' | 'info' | 'neutral';

interface WorkflowCardProps {
    title: string;
    descriptionPoints: string[];
    buttonLabel: string;
    onButtonClick?: () => void;
    icon?: React.ReactNode;
    iconVariant?: WorkflowCardVariant;
    isDisabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
    'data-testid'?: string;
    buttonTestId?: string;
}

const iconVariantClasses: Record<WorkflowCardVariant, string> = {
    warning: 'bg-[#fff9e7] border-[#efb306]',
    success: 'bg-[#e9f8f3] border-[#34be91]',
    info: 'bg-[#e8f3fb] border-[#3592d3]',
    neutral: 'bg-greyBackground border-borderGrey',
};

const col = 'flex flex-col';
const colGap4 = 'flex flex-col gap-4';

export const WorkflowCard: FC<WorkflowCardProps> = ({
    title,
    descriptionPoints,
    buttonLabel,
    onButtonClick,
    icon,
    iconVariant = 'warning',
    isDisabled = false,
    style,
    className,
    'data-testid': testId,
    buttonTestId,
}) => {
    return (
        <div
            className={`${col} gap-6 rounded-xl border border-borderGrey px-[17px] py-[25px] ${isDisabled ? 'bg-greyAlt1 opacity-80' : 'bg-white'} ${className ?? ''}`}
            style={style}
            data-testid={testId}
        >
            <div className={`${col} gap-2`}>
                <div className={`flex size-[42px] shrink-0 items-center justify-center rounded-xl border ${iconVariantClasses[iconVariant]}`}>
                    {icon}
                </div>
                <div className={colGap4}>
                    <h3 className={`text-base font-bold leading-8 m-0 ${isDisabled ? 'text-greyFont' : 'text-black'}`}>{title}</h3>
                    <div className={colGap4}>
                        {descriptionPoints.map((point, i) => (
                            <div key={i} className={`flex items-center gap-2 text-base`}>
                                <span className="flex shrink-0 items-center justify-center [&>svg]:size-4">
                                    <CheckIcon color={isDisabled ? 'grey' : '#45338a'} />
                                </span>
                                {point}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Button
                type="primary"
                onClick={onButtonClick}
                disabled={isDisabled}
                className={`h-8 w-full !rounded-[6px] border-purpleFont text-base font-semibold !shadow-[0px_2px_0px_0px_rgba(95,73,188,0.1)] ${isDisabled ? '!bg-black/5 !border-greyBackground !text-black/25 !shadow-none' : '!bg-purpleFont'}`}
                data-testid={buttonTestId}
            >
                <div className='flex justify-center items-center'>
                    {buttonLabel}
                    <ArrowRight className="ml-2 text-xs" strokeWidth='2' />
                </div>
            </Button>
        </div>
    );
};

export default WorkflowCard;
